import { useState, useEffect, useRef, useCallback } from "react";
import InputOptions from "../../../utils/InputOptions";
import InputRange from "../../../utils/InputRange";
import useMAFSimulationData from "../../hooks/useMAFSimulationData";
import { CurrencyFormat } from "../../../utils";

/**
 * Simulador de financiamiento MAF para v2
 * Permite configurar pie y número de cuotas para simular crédito
 * Auto-simula con valores por defecto solo después de completar datos personales
 */
const MAFSimulation = ({ onResultChange, isHidden = false, autoSimulateOnMount = false }) => {
    const {
        mafData,
        updateDownpayment,
        updateFees,
        simulateCredit,
        getInputConfig,
        isSimulationCurrent,
        resetForEdit,
        updateMAFData,
    } = useMAFSimulationData();

    const inputConfig = getInputConfig();

    // Estados locales para simulación
    const [simulationData, setSimulationData] = useState({
        downpayment: mafData.downpayment_percentage,
        fees: mafData.number_fees,
    });
    const [showResult, setShowResult] = useState(mafData.hasResult);
    const [showTerms, setShowTerms] = useState(false);
    
    // Usar ref para evitar múltiples auto-simulaciones
    const hasAutoSimulatedRef = useRef(false);
    
    // Ref para mantener la función simulateCredit estable
    const simulateCreditRef = useRef(simulateCredit);
    
    // Actualizar la ref cuando simulateCredit cambie
    useEffect(() => {
        simulateCreditRef.current = simulateCredit;
    }, [simulateCredit]);

    // Auto-simulación al montar el componente si no hay resultado previo
    useEffect(() => {
        // Solo auto-simular si:
        // 1. autoSimulateOnMount está habilitado
        // 2. No se ha auto-simulado antes (usando ref)
        // 3. No hay resultado previo
        // 4. El componente no está oculto
        // 5. Tenemos datos de simulación válidos
        if (
            autoSimulateOnMount && 
            !hasAutoSimulatedRef.current && 
            !mafData.hasResult && 
            !isHidden &&
            simulationData.downpayment &&
            simulationData.fees
        ) {
            // console.log("MAF: Auto-simulando con valores por defecto", {
            //     downpayment: simulationData.downpayment,
            //     fees: simulationData.fees,
            //     mafData
            // });
            hasAutoSimulatedRef.current = true;
            
            // Ejecutar simulación con valores por defecto después de un pequeño delay
            // para asegurar que todos los datos necesarios estén disponibles
            const timer = setTimeout(() => {
                simulateCreditRef.current({
                    downpayment_percentage: simulationData.downpayment,
                    number_fees: simulationData.fees,
                }, true);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [autoSimulateOnMount, mafData.hasResult, isHidden, simulationData.downpayment, simulationData.fees]);

    // Si el monto financiado cambió después de simular (accesorios, mantenciones
    // prepagadas, plan renueve), el resultado mostrado ya no corresponde a la
    // configuración actual: volvemos al formulario para forzar una nueva simulación
    // en lugar de dejar a la vista una cuota que no se puede respetar.
    useEffect(() => {
        if (mafData.hasResult && !isSimulationCurrent) {
            resetForEdit();
        }
    }, [mafData.hasResult, isSimulationCurrent, resetForEdit]);

    // Sincronizar datos locales con el estado global de MAF
    useEffect(() => {
        setSimulationData({
            downpayment: mafData.downpayment_percentage,
            fees: mafData.number_fees,
        });
        setShowResult(mafData.hasResult);
    }, [mafData.downpayment_percentage, mafData.number_fees, mafData.hasResult]);

    // Notificar cambios en el resultado al componente padre
    useEffect(() => {
        if (onResultChange) {
            onResultChange(showResult);
        }
    }, [showResult, onResultChange]);

    // Ejecuta la simulación de crédito con los datos actuales
    const handleSimulation = async (isAutoSimulation = false) => {
        // Solo enviar evento de dataLayer si es una simulación manual (no automática)
        if (!isAutoSimulation && dataLayer) {
            dataLayer.push({
                'event': 'cotizador',
                'eventCategory': 'opciones_de_pago',
                'eventAction': 'cta',
                'eventLabel': 'simula_financiamiento'
            });
        }
        
        await simulateCredit({
            downpayment_percentage: simulationData.downpayment,
            number_fees: simulationData.fees,
        }, isAutoSimulation);
    };

    // Maneja el cambio en el porcentaje de pie
    const handleChangeDownpayment = (value) => {
        const newData = { ...simulationData, downpayment: value };
        setSimulationData(newData);
        updateDownpayment(value);
        if (mafData.error) updateMAFData({ error: null });
    };

    // Maneja el cambio en el número de cuotas
    const handleChangeFees = (value) => {
        const newData = { ...simulationData, fees: value };
        setSimulationData(newData);
        updateFees(value);
        if (mafData.error) updateMAFData({ error: null });
    };

    const toggleTerms = () => {
        setShowTerms((prev) => !prev);
    };

    return (
        <div className={`maf-simulation ${isHidden ? "hidden" : ""}`} id="maf-simulation">
            <div className="component-body">
                <div className="mini-simulator">
                    {mafData.error ? (
                        // Mismo tratamiento que Kinto: el bloque SCSS
                        // `.maf-simulation, .kinto-simulation` es compartido
                        <div className="simulation-result">
                            <div className="error-message">
                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px' }}>
                                    <circle cx="24" cy="24" r="22" stroke="#c53030" strokeWidth="2" fill="none"/>
                                    <path d="M24 14V26" stroke="#c53030" strokeWidth="2" strokeLinecap="round"/>
                                    <circle cx="24" cy="32" r="1.5" fill="#c53030"/>
                                </svg>
                                <p className="error-title">No se pudo realizar la simulación</p>
                                <p className="error-text">
                                    No pudimos simular tu financiamiento en este momento. Por
                                    favor, inténtalo nuevamente.
                                </p>
                                <button
                                    className="btn outline-black retry-button"
                                    onClick={() => updateMAFData({ error: null })}
                                    type="button"
                                >
                                    Volver a simular
                                </button>
                            </div>
                        </div>
                    ) : !showResult ? (
                        <>
                            <div className="inputs-container">
                                <InputRange
                                    onChangeValue={handleChangeDownpayment}
                                    title="Porcentaje de Pie"
                                    subtitle={`Hasta un ${inputConfig.maxDownpayment}% del valor total del vehículo.`}
                                    valueText="Mínimo"
                                    valueTextMax="Máximo"
                                    suffix="%"
                                    defaultValue={simulationData.downpayment}
                                    minValue={inputConfig.minDownpayment}
                                    maxValue={inputConfig.maxDownpayment}
                                />
                                <InputOptions
                                    onChangeValue={handleChangeFees}
                                    title="Cantidad de Cuotas"
                                    subtitle="Seleccione en cuántas cuotas desea comprar su vehículo."
                                    options={[
                                        {
                                            value: inputConfig.minFees,
                                            label: `${inputConfig.minFees} cuotas`,
                                        },
                                        {
                                            value: inputConfig.maxFees,
                                            label: `${inputConfig.maxFees} cuotas`,
                                        },
                                    ]}
                                    defaultValue={simulationData.fees}
                                />
                            </div>
                            <div className="button-container">
                                {mafData.isLoading ? (
                                    <div className="loading-spinner">
                                        <div className="spinner"></div>
                                    </div>
                                ) : (
                                    <button
                                        className="btn outline-black"
                                        onClick={() => handleSimulation(false)}
                                        type="button"
                                        disabled={mafData.isLoading}
                                    >
                                        Simula y preaprueba tu financiamiento
                                    </button>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="simulation-result">
                            <p className="title">
                                {mafData.number_fees} cuotas de{" "}
                                {CurrencyFormat(mafData.fee_value || 0)}
                            </p>
                            <p className="plus">
                                + Valor del pie: {CurrencyFormat(mafData.downpayment_amount || 0)} ({mafData.downpayment_percentage}%)
                            </p>
                            <p className="plus">
                                + Cuota Final:{" "}
                                {CurrencyFormat(mafData.future_value_guaranteed || 0)} | CAE: {mafData.equivalent_annual_load || 0}%
                            </p>
                            <p className="plus">
                                + Costo Total del Crédito:{" "}
                                {CurrencyFormat(mafData.credit_total_cost || 0)}
                            </p>
                            <div className="terms">
                                <div className="visible-part" onClick={toggleTerms}>
                                    <p>Ver términos y condiciones</p>
                                    <svg
                                        width="14"
                                        height="9"
                                        viewBox="0 0 14 9"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M13.4766 7.3605C13.4752 7.8226 13.2098 8.23746 12.8045 8.41358C12.3991 8.58934 11.9325 8.49194 11.6211 8.16752L6.94704 3.25839L2.27295 8.16752C1.84183 8.55638 1.19688 8.53075 0.795711 8.10747C0.393144 7.68529 0.368419 7.00752 0.738599 6.55385L6.17987 0.832566C6.60437 0.389145 7.28971 0.389145 7.71422 0.832566L13.1555 6.55384C13.3596 6.76695 13.4752 7.05732 13.4766 7.3605Z"
                                            fill="#191919"
                                        />
                                    </svg>
                                </div>
                                <div className={"hidden-part" + (showTerms ? " active" : "")}>
                                    <p>
                                        Los valores y conceptos indicados en esta simulación son
                                        referenciales, no vinculantes y no conllevan la aprobación y
                                        otorgamiento del crédito, el cual es gestionado y otorgado
                                        por MAF Chile. El Valor Cuota incluye gastos operacionales e
                                        impuestos al crédito. Las cuales se encuentran calculadas
                                        con vencimiento en 30 días. El monto de la cuota para
                                        financiamiento automotriz es meramente referencial y no
                                        vinculante, los montos en pesos chilenos informados y demás
                                        condiciones del crédito están sujetas a variación en función
                                        del monto y plazo en que se tome el crédito. No considera
                                        meses de gracia o pago diferido. Los valores informados
                                        incluyen impuesto al crédito, gastos o cargos propios del
                                        crédito, no incluye cargos por productos o servicios
                                        voluntariamente contratados.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MAFSimulation;
